import moment from 'moment';
import * as d3 from 'd3';
import React, { Component } from 'react';

import { getClass } from '../../../utils/component-helpers';
import { formatValue } from '../../base/common/utils';

class Graph extends Component {
  constructor(props) {
    super(props);

    this.graphColors = {
      publisher_total_earnings: {
        gradient: ['rgb(48, 35, 174)', 'rgb(200, 109, 215)'],
        pattern: '#7404C2',
        label: '#5D04AB',
      },
      total_attributed_revenue: {
        gradient: ['rgb(246, 166, 35)', 'rgb(252, 195, 101)'],
        pattern: '#EE9B13',
        label: '#F5A623',
      },
      clicks: {
        gradient: ['rgb(11, 83, 173)', 'rgb(20, 147, 215)'],
        pattern: '#3873EE',
        label: '#0942A4',
      },
      publisher_epc: {
        gradient: ['rgb(11, 68, 10)', 'rgb(32, 191, 112)'],
        label: '#107426',
      },
      revenue: {
        gradient: ['rgb(246, 166, 35)', 'rgb(252, 195, 101)'],
        pattern: '#EE9B13',
        label: '#F5A623',
      },
      rpc: {
        gradient: ['rgb(11, 68, 10)', 'rgb(32, 191, 112)'],
        label: '#107426',
      },
      spend: {
        gradient: ['rgb(48, 35, 174)', 'rgb(200, 109, 215)'],
        pattern: '#7404C2',
        label: '#5D04AB',
      },
      count_per_date: {
        gradient: ['#000A4E', '#0E0077'],
      },
    };

    // Ratios and Numbers
    this.barWidthRatio = 0.4; // Ratio of bar width / this.x.bandwidth()
    this.marginRatio = 0.3; // Ratio of bar margin / this.x.bandwidth()
    this.maxNumLabels = 15; // Max # of labels before removing/adding labels/tooltips
    this.largeTooltipWidth = 186;
    this.smallTooltipWidth = 111;
    this.graphMargin = 40; // 40px margin on each side of the graph
    this.xMargin = this.roasRequired() ? 50 : 20;
    this.x2Margin = this.roasRequired() ? 75 : 40;
    this.roaMargin = 20;
    this.roaWidth = 48;
    this.barWidth = null;
    this.barMargin = null;

    // Nodes and tooltip
    this.svg = null;
    this.graphNode = null;
    this.tooltipNode = null;
    this.tooltipIndex = null;
    this.tooltipWidth = null;
    this.barPositions = [];

    // Shortcuts
    this.formatDisplayDate = d3.timeFormat('%a, %b %e'); // ex: 'Mon, Apr 12'
    this.graphType = this.props.graphType; // clicks | publisher_epc | ...
    this.data = this.props.data;

    // Bindings
    this.getTooltipPosition = this.getTooltipPosition.bind(this);
    this.hideTooltip = this.hideTooltip.bind(this);

    this.state = {
      tooltip: {
        date: null,
        nextDate: null,
        data: [],
        large: false,
        arrowRight: false,
      },
    };
  }

  componentDidMount() {
    this.initializeGraph();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.graphType !== this.props.graphType ||
      prevProps.rangeType !== this.props.rangeType
    ) {
      this.graphType = this.props.graphType;
      this.data = this.props.data;

      this.tooltipIndex = null;
      this.tooltipNode.style.display = 'none';
      this.xMargin = this.roasRequired() ? 50 : 20;
      this.x2Margin = this.roasRequired() ? 75 : 40;
      this.tooltipListeners('remove');
      this.initializeGraph();
    }
  }

  initializeGraph() {
    d3.selectAll(`.graph.${this.graphType} *`).remove(); // Clear graph

    this.svg = d3.select(`.graph.${this.graphType}`);
    this.svg
      .attr('width', '1500')
      .attr('height', '440')
      .attr('viewBox', '0 0 1500 450')
      .attr('preserveAspectRatio', 'xMidYMid meet');

    // Return actual dimensions of graph on page
    this.width = parseInt(this.svg.attr('width'));
    this.height = parseInt(this.svg.attr('height'));
    // Reduce height for the below calculations, for aesthetic reasons
    this.height -= 40;

    this.appendAxes();
    this.createGradient();
    this.addBars();
    this.addRoas();
    this.addProjection();
    this.addLabels();
  }

  appendAxes() {
    const yValues = this.data.map(stat => parseFloat(stat.stats[this.graphType]));
    const projectedY = this.props.projections[this.graphType] || 0;
    const maxYValue = d3.max(yValues.concat(projectedY)) || 1;

    this.y = d3
      .scaleLinear()
      .domain([0, maxYValue * 1.5])
      .range([this.height, 0]);
    this.x = d3
      .scaleBand()
      .domain(this.data.map(data => data.dateLabel))
      .range([0, this.width - this.graphMargin * 2]);

    const yAxis = this.svg
      .append('g')
      .attr('class', 'axis axis-y')
      .call(d3.axisLeft(this.y).tickSize(0));

    this.svg
      .append('g')
      .attr('class', 'axis axis-x')
      .attr('transform', `translate(${this.graphMargin}, ${this.height + this.xMargin})`)
      .call(d3.axisBottom(this.x).tickSize(0));

    // Append a second axis for secondary date labels
    this.svg
      .append('g')
      .attr('class', 'axis axis-x-2')
      .attr('transform', `translate(${this.graphMargin}, ${this.height + this.x2Margin})`)
      .call(
        d3
          .axisBottom(this.x)
          .tickSize(0)
          .tickFormat((_, i) => {
            if (this.data[i].secondLabel) {
              return this.data[i].secondLabel;
            }
            return '';
          }),
      );

    yAxis.selectAll('text').remove(); // Remove y-axis text values

    // Build grid lines
    this.svg
      .append('g')
      .attr('class', 'grid-lines')
      .call(
        d3
          .axisLeft(this.y)
          .tickSize(-this.width)
          .tickFormat(''),
      );
  }

  addRoas() {
    // Append a ROA axis for merchant graph
    if (!this.roasRequired()) return;

    // Axis
    this.svg
      .append('g')
      .attr('class', 'axis axis-roa')
      .attr('transform', `translate(${this.graphMargin}, ${this.height + this.roaMargin})`)
      .call(
        d3
          .axisBottom(this.x)
          .tickSize(0)
          .tickFormat((something, i) => {
            return parseFloat(this.data[i].stats.roa).toFixed(1);
          }),
      );

    // Background coloring for axis
    const roaMargin = this.x.bandwidth() / 2 - this.roaWidth / 2;
    this.svg
      .selectAll('.roa-rect')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('class', 'roa-rect')
      .attr('x', d => this.x(d.dateLabel) + roaMargin)
      .attr('y', 417)
      .attr('width', this.roaWidth)
      .attr('height', 24)
      .attr('rx', 12)
      .attr('transform', `translate(${this.graphMargin}, 0)`)
      .style('fill', 'rgba(0, 60, 166, .08)');

    // Roas label
    this.svg
      .append('text')
      .attr('class', 'roa-label')
      .attr('x', this.props.rangeType === 'thisYear' ? 20 : 50)
      .attr('y', 433)
      .text('ROAS');
  }

  addBars() {
    // Append the bars of the graph, and apply the proper gradient
    this.barWidth = Math.min(Math.max(this.x.bandwidth() * this.barWidthRatio, 20), 68); // Min bar width of 20, max bar width of 68
    this.barMargin = this.x.bandwidth() / 2 - this.barWidth / 2;

    this.svg
      .selectAll('.bar')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => this.x(d.dateLabel) + this.barMargin)
      .attr('width', this.barWidth)
      .attr('y', d => this.y(d.stats[this.graphType]))
      .attr('height', d => this.height - this.y(d.stats[this.graphType]))
      .attr('transform', `translate(${this.graphMargin}, 0)`)
      .style('fill', d => `url(#month-gradient-${this.graphType})`);
  }

  addProjection() {
    if (!this.props.projections[this.graphType] || !this.data.length) return;

    const originalData = this.data[this.data.length - 1];
    const projectedY = this.y(this.props.projections[this.graphType]);
    const earningsY = this.y(originalData.stats[this.graphType]);
    const xBandwith = this.x.bandwidth(); // Distance btwn x axis ticks

    this.svg
      .append('rect')
      .attr('class', 'projected-bar')
      .attr('x', this.x(originalData.dateLabel) + this.barMargin)
      .attr('width', this.barWidth)
      .attr('y', projectedY)
      .attr('height', Math.max(earningsY - projectedY, 0))
      .attr('transform', `translate(${this.graphMargin}, 0)`)
      .style('fill', d => 'url(#striped-pattern)');

    if (this.tooltipsNeeded()) return;

    // Build opaque container that lies under this month's value label
    this.svg
      .append('rect')
      .attr('class', 'opaque-container')
      .attr('height', 20)
      .attr('width', xBandwith * this.barWidthRatio)
      .attr('y', earningsY - 25)
      .attr('x', this.x(originalData.dateLabel) + this.barMargin)
      .style('fill', 'rgba(255, 255, 255, 0.9)');
  }

  addLabels() {
    if (this.tooltipsNeeded()) {
      this.getBarPositions();
      this.tooltipListeners('add');
      return;
    }

    const projection = this.props.projections[this.graphType];
    const labelSpacing = 10;
    const xSpacing = this.x.bandwidth() / 2;
    const rectLabels = this.svg.append('g').attr('class', 'data-labels');

    rectLabels
      .selectAll('data-labels')
      .data(this.data)
      .enter()
      .append('text')
      .attr('class', 'value-label')
      .attr('x', d => this.x(d.dateLabel) + xSpacing)
      .attr('y', d => this.y(d.stats[this.graphType]) - labelSpacing)
      .text(d => this.formatNumber(d.stats[this.graphType], this.graphType))
      .attr('transform', `translate(${this.graphMargin}, 0)`);

    if (!projection) return;

    // Determine if projection label will need additional spacing (prevent overlap)
    const lastDataPoint = this.data[this.data.length - 1];
    const lastDataPointY = this.y(lastDataPoint.stats[this.graphType]);
    const yDiff = Math.abs(this.y(projection) - lastDataPointY);
    const additionalYSpacing = yDiff < 20 ? 20 - yDiff : 0;

    // Add label for projection
    rectLabels
      .append('text')
      .attr('class', 'projected-label')
      .attr('x', this.x(lastDataPoint.dateLabel) + xSpacing)
      .attr('y', this.y(projection) - labelSpacing - additionalYSpacing)
      .text(this.formatNumber(projection, this.graphType))
      .style('fill', this.graphColors[this.graphType].pattern)
      .attr('transform', `translate(${this.graphMargin}, 0)`);

    // Adjust opaque container to the size of labels
    const labels = document.querySelectorAll('.value-label');
    const lastLabel = labels[labels.length - 1];
    const containerWidth = lastLabel ? lastLabel.getBoundingClientRect().width + 10 : 0;
    const translation = this.graphMargin + (this.barWidth - containerWidth) / 2;

    d3.select('.opaque-container')
      .attr('width', containerWidth)
      .attr('transform', `translate(${translation}, 0)`);
  }

  /* Tooltip Methods */
  tooltipsNeeded() {
    return (
      ['lastMonth', 'thisMonth'].includes(this.props.rangeType) &&
      this.data.length > this.maxNumLabels
    );
  }

  getBarPositions() {
    this.barPositions = [];

    [...document.querySelectorAll(`.graph.${this.graphType} .bar`)].forEach((node, i, arr) => {
      // Use projected-bar for final bar position, if applicable
      if (
        arr.length - 1 === i &&
        this.props.rangeType === 'thisMonth' &&
        this.props.projections[this.graphType]
      ) {
        node = document.querySelector(`.graph.${this.graphType} .projected-bar`);
      }

      const { top, left, width } = node.getBoundingClientRect();
      const xPosition = left + width / 2;

      this.barPositions.push({
        xPos: xPosition,
        top,
        width,
      });
    });
  }

  getTooltipPosition(event) {
    let index = null;
    let minDiff = null;

    this.barPositions.forEach((obj, i) => {
      const diff = Math.abs(obj.xPos - event.x);
      if (minDiff === null || diff < minDiff) {
        minDiff = diff;
        index = i;
      }
    });

    if (index === this.tooltipIndex) return;

    this.tooltipIndex = index;
    this.updateTooltipState();
  }

  updateTooltipState() {
    // Evaluate new data for tooltip
    const data = this.data[this.tooltipIndex].stats;
    const date = this.formatDisplayDate(moment(data.event_date));
    const value = data[this.graphType];
    const isLastIndex = this.data.length - 1 === this.tooltipIndex;
    const pacing =
      isLastIndex && this.props.projections[this.graphType]
        ? this.props.projections[this.graphType]
        : null;

    // Determine position variables that apply to state
    const { left, width } = this.graphNode.getBoundingClientRect();
    const barPosition = this.barPositions[this.tooltipIndex];
    const tooltipWidth = pacing !== null ? this.largeTooltipWidth : this.smallTooltipWidth;
    const overMaxWidth = barPosition.xPos + tooltipWidth / 2 > left + width;
    const tooltipBottom = window.innerHeight - barPosition.top + 14;
    const tooltipLeft = overMaxWidth
      ? barPosition.xPos - tooltipWidth + barPosition.width / 2 + 10
      : barPosition.xPos - tooltipWidth / 2;

    this.setState(
      {
        tooltip: {
          date,
          value,
          pacing,
          large: pacing !== null,
          arrowRight: overMaxWidth,
        },
      },
      () => {
        this.moveTooltip(tooltipBottom, tooltipLeft);
      },
    );
  }

  moveTooltip(tooltipBottom, tooltipLeft) {
    Object.assign(this.tooltipNode.style, {
      display: 'flex',
      bottom: `${tooltipBottom}px`,
      left: `${tooltipLeft}px`,
    });
  }

  hideTooltip(event) {
    if (event && event.toElement && Array.from(event.toElement.classList).includes('graph-tooltip'))
      return;

    this.tooltipIndex = null;
    this.tooltipNode.style.display = 'none';
  }

  tooltipListeners(action) {
    const method = action === 'add' ? 'addEventListener' : 'removeEventListener';
    if (this.graphNode) {
      this.graphNode[method]('mousemove', this.getTooltipPosition);
      this.graphNode[method]('mouseleave', this.hideTooltip);
    }
    if (this.tooltipNode) {
      this.tooltipNode[method]('mousemove', this.getTooltipPosition);
    }
  }

  /* Utility Methods */
  createGradient() {
    // Create html gradients that will later be added to the bars via 'fill'
    const { gradient } = this.graphColors[this.graphType];
    const { pattern } = this.graphColors[this.graphType];
    const defs = this.svg.append('defs');

    if (pattern) this.createPattern(pattern, defs);

    const linearGradient = defs
      .append('linearGradient')
      .attr('id', `month-gradient-${this.graphType}`)
      .attr('x1', '0%')
      .attr('x2', '0%')
      .attr('y1', '0%')
      .attr('y2', '100%');

    linearGradient
      .selectAll('stop')
      .data(gradient)
      .enter()
      .append('stop')
      .style('stop-color', color => color)
      .attr('offset', (color, i) => `${(i / (gradient.length - 1)) * 100}%`);
  }

  createPattern(pattern, defs) {
    // Striped pattern for projected view
    defs
      .append('pattern')
      .attr('id', 'striped-pattern')
      .attr('width', '5')
      .attr('height', '8')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(120)')
      .append('rect')
      .attr('width', '1')
      .attr('height', '8')
      .attr('transform', 'translate(0, 0)')
      .style('fill', pattern);
  }

  formatNumber(value, fieldName) {
    const type = fieldName === 'clicks' || fieldName === 'count_per_date' ? 'number' : 'currency';

    return formatValue(value, type, fieldName);
  }

  roasRequired() {
    return (
      this.props.merchantGraph &&
      !['sevenDay', 'lastMonth', 'thisMonth'].includes(this.props.rangeType)
    );
  }

  render() {
    const value = this.formatNumber(this.state.tooltip.value, this.graphType);
    const pacing = this.formatNumber(this.state.tooltip.pacing, this.graphType);
    let valueDiv = <div className="value">{value}</div>;
    let pacingDiv = null;

    if (this.state.tooltip.large) {
      pacingDiv = (
        <div className="pacing">
          <span className="label">Pacing</span>
          <span>{pacing}</span>
        </div>
      );
      valueDiv = (
        <div className="value">
          <span className="label">{this.props.graphLabelMap[this.graphType]}</span>
          <span>{value}</span>
        </div>
      );
    }

    return (
      <>
        <svg
          className={`graph ${this.props.graphType}`}
          ref={node => (this.graphNode = node)}
        ></svg>
        <div
          ref={node => (this.tooltipNode = node)}
          className={getClass('graph-tooltip', this.graphType, {
            large: this.state.tooltip.large,
            'arrow-right': this.state.tooltip.arrowRight,
          })}
        >
          <div className="date">{this.state.tooltip.date}</div>
          {valueDiv}
          {pacingDiv}
        </div>
      </>
    );
  }
}

export default Graph;
